import { useTheme } from "@emotion/react";
import useStyles from "./styles";

import {
  ArticleOutlined,
  CheckCircleOutlineOutlined,
  ContentCopyOutlined,
  DriveFileRenameOutlineOutlined,
  PersonOutlineOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  Chip,
  Grid,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import dayjs from "dayjs";
import { useState } from "react";
import { formatDescricao } from "../../../utils/formatTicket";
import { maskCpfCnpj, maskPhone } from "../../../utils/masks";
import copyTexto from "../../../utils/copyTexto";
import CopyButton from "../shared/copyButton";
import InfoField from "../shared/infoField";
import SectionCard from "../shared/sectionCard";

export default function DescricaoTicketComponent(params) {
  const { ticketDetails, erp = "", isOsIxc = false } = params;
  const theme = useTheme();
  const styles = useStyles(theme);
  // O componente não tinha canal de aviso próprio; a cópia precisa de um para
  // responder igual ao resto do sistema.
  const [snackbar, setSnackbar] = useState(null);
  function htmlForText(html) {
    if (!html) return "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    function extrairTexto(node) {
      let texto = "";

      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          texto += child.textContent;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const tag = child.tagName;

          switch (tag) {
            case "BR":
              texto += "\n";
              break;
            case "P":
            case "DIV":
              texto += extrairTexto(child) + "\n";
              break;
            case "LI":
              texto += "- " + extrairTexto(child) + "\n";
              break;
            case "UL":
            case "OL":
              texto += extrairTexto(child) + "\n";
              break;
            default:
              texto += extrairTexto(child);
          }
        }
      });

      return texto;
    }

    // Extrai texto e normaliza múltiplas quebras de linha
    const resultado = extrairTexto(tempDiv);
    return resultado.replace(/\n{2,}/g, "\n").trim();
  }

  const prioridadeConfig = {
    Baixa: (
      <Tooltip title="Prioridade baixa">
        <Chip
          sx={{
            ...styles.chipPriority,
            backgroundColor: theme.palette.colors.blue.medium,
          }}
          size="small"
          label="Baixa"
        />
      </Tooltip>
    ),
    Normal: (
      <Tooltip title="Prioridade normal">
        <Chip
          sx={{
            ...styles.chipPriority,
            backgroundColor: theme.palette.colors.pink.medium,
          }}
          size="small"
          label="Normal"
        />
      </Tooltip>
    ),
    Alta: (
      <Tooltip title="Prioridade alta ">
        <Chip
          sx={{
            ...styles.chipPriority,
            backgroundColor: theme.palette.colors.orange.medium,
          }}
          size="small"
          label="Alta"
        />
      </Tooltip>
    ),
    Crítica: (
      <Tooltip title="Prioridade crítica">
        <Chip
          sx={{
            ...styles.chipPriority,
            backgroundColor: theme.palette.colors.red.medium,
          }}
          size="small"
          label="Crítica"
        />
      </Tooltip>
    ),
  };
  const chipSla = (value) => {
    if (value === 100) {
      return (
        <Chip
          sx={{
            ...styles.chipSla,
            backgroundColor: theme.palette.chip.main.completed,
          }}
          label={`${value}%`}
          size="small"
        />
      );
    } else if (value < 100 && value >= 80) {
      return (
        <Chip
          sx={{
            ...styles.chipSla,
            backgroundColor: theme.palette.colors.yellow.darker,
          }}
          label={`${value}%`}
          size="small"
        />
      );
    } else if (value < 80 && value >= 50) {
      return (
        <Chip
          sx={{
            ...styles.chipSla,
            backgroundColor: theme.palette.chip.main.inProgress,
          }}
          label={`${value}%`}
          size="small"
        />
      );
    } else if (value < 50 && value >= 0) {
      return (
        <Chip
          sx={{
            ...styles.chipSla,
            backgroundColor: theme.palette.chip.main.waitingReturn,
          }}
          label={`${value}%`}
          size="small"
        />
      );
    } else if (value < 0) {
      return (
        <Chip
          sx={{
            ...styles.chipSla,
            backgroundColor: theme.palette.chip.main.pending,
          }}
          label={`${value}%`}
          size="small"
        />
      );
    } else {
      return <Chip sx={{ ...styles.chipSla }} label={""} />;
    }
  };

  // Os campos viram dados em vez de JSX repetido. Isso resolve três coisas de
  // uma vez: a divisão entre "cliente" e "atendimento" fica explícita, o botão
  // de copiar serializa a mesma lista que a tela mostra (sem risco de divergir)
  // e cada campo passa a fluir na grade conforme a largura, em vez de estar
  // preso a uma das duas colunas fixas de antes.
  const responsaveis = ticketDetails?.usuarios_responsaveis || [];

  const camposCliente = [
    {
      label: "Nome",
      value: ticketDetails?.cliente?.nome || "",
      copiavel: true,
    },
    {
      label: "CPF / CNPJ",
      value: maskCpfCnpj(ticketDetails?.cliente?.cnpj || ""),
      copiavel: true,
    },
    {
      label: "Telefone do contato",
      value: maskPhone(ticketDetails?.cliente?.telefone_primario || ""),
      copiavel: true,
    },
    erp === "HUBSOFT" && {
      label: "E-mail",
      value: ticketDetails?.cliente?.email_principal,
      copiavel: true,
    },
    {
      label: erp === "IXC" ? "Endereço" : "Endereço de instalação",
      value: ticketDetails?.cliente?.endereco_instalacao,
      copiavel: true,
    },
    erp === "HUBSOFT" && {
      label: "Serviço",
      value: `${ticketDetails?.cliente_servico?.id_cliente_servico || ""} - ${
        ticketDetails?.cliente_servico?.descricao || ""
      }`,
      copiavel: true,
    },
  ].filter(Boolean);

  const camposAtendimento = [
    {
      label: "Aberto por",
      value: ticketDetails?.usuario_abertura,
    },
    {
      label: "Data de cadastro",
      value: ticketDetails?.data_cadastro_timestamp
        ? dayjs(ticketDetails?.data_cadastro_timestamp).format("DD/MM/YY HH:mm")
        : "",
    },
    ticketDetails?.data_fechamento_timestamp && {
      label: "Data de encerramento",
      value: ticketDetails?.data_fechamento_timestamp
        ? dayjs(ticketDetails?.data_fechamento_timestamp).format(
            "DD/MM/YY HH:mm",
          )
        : "",
    },
    erp === "HUBSOFT" && {
      label: "Responsável",
      value: responsaveis.map((user) => user?.name).join(", "),
      tooltip: responsaveis.length ? (
        <>
          {responsaveis.map((user) => (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
              key={user?.id}
            >
              • {user?.name}
            </Typography>
          ))}
        </>
      ) : (
        ""
      ),
      // Mostra o primeiro e um contador, para não estourar a célula quando o
      // atendimento tem muitos responsáveis.
      children: responsaveis.length ? (
        <Box sx={styles.boxResponsavel}>
          <Box component="span" sx={styles.nomeResponsavel}>
            {responsaveis[0]?.name || ""}
          </Box>
          {responsaveis.length > 1 && (
            <Box component="span" sx={styles.boxCont}>
              +{responsaveis.length - 1}
            </Box>
          )}
        </Box>
      ) : null,
    },
    erp === "HUBSOFT" && {
      label: "Origem do contato",
      value: ticketDetails?.origem_contato?.descricao,
    },
    erp === "IXC" && {
      label: "Melhor horário",
      value: ticketDetails?.melhorHorario,
    },
    erp === "IXC" && {
      label: "SLA",
      value: ticketDetails?.sla !== undefined ? `${ticketDetails?.sla}%` : "",
      children: (
        <Box sx={styles.boxChipCampo}>{chipSla(ticketDetails?.sla)}</Box>
      ),
    },
    erp === "IXC" && {
      label: "Prioridade",
      value: ticketDetails?.prioridade,
      children: prioridadeConfig[ticketDetails?.prioridade] ? (
        <Box sx={styles.boxChipCampo}>
          {prioridadeConfig[ticketDetails?.prioridade]}
        </Box>
      ) : null,
    },
    erp === "IXC" && {
      label: isOsIxc ? "Setor" : "Departamento",
      value: ticketDetails?.setor,
    },
  ].filter(Boolean);

  const camposVoalle = [
    { label: "Cliente", value: ticketDetails?.geral?.cliente, copiavel: true },
    { label: "Solicitante", value: ticketDetails?.geral?.solicitante },
    {
      label: "Abertura",
      value: ticketDetails?.geral?.abertura
        ? dayjs(ticketDetails?.geral?.abertura).format("DD/MM/YYYY HH:mm")
        : "",
    },
    {
      label: "Prazo (SLA)",
      value: ticketDetails?.geral?.prazoSla
        ? dayjs(ticketDetails?.geral?.prazoSla).format("DD/MM/YYYY HH:mm")
        : "",
    },
    { label: "Etiqueta", value: ticketDetails?.geral?.etiqueta },
    { label: "Área", value: ticketDetails?.geral?.area },
    { label: "Equipe", value: ticketDetails?.geral?.equipe },
    { label: "Local", value: ticketDetails?.geral?.local },
    { label: "Atendente", value: ticketDetails?.geral?.responsavel },
    {
      label: "Tipo de solicitação",
      value: ticketDetails?.geral?.typeSolicitacao,
    },
  ];

  // Copia a seção inteira como "Rótulo: valor" por linha, que é o formato que
  // o suporte cola em e-mail ou conversa.
  const copiarCampos = (nomeSecao, campos) => {
    const texto = [
      nomeSecao,
      ...campos.map(
        (campo) => `${campo.label}: ${campo.value || "não informado"}`,
      ),
    ].join("\n");

    copyTexto({
      campo: texto,
      setSnackbar,
      mensagem: `${nomeSecao} copiados!`,
    });
  };

  return (
    <Box sx={styles.containerBodyModal}>
      {(erp === "HUBSOFT" || erp === "IXC") && (
        <>
          <SectionCard
            icon={<PersonOutlineOutlined sx={styles.iconSecao} />}
            title="Dados do cliente"
            action={
              <Button
                size="small"
                variant="text"
                startIcon={<ContentCopyOutlined sx={{ fontSize: "16px" }} />}
                onClick={() => copiarCampos("Dados do cliente", camposCliente)}
                sx={styles.botaoCopiarSecao}
              >
                Copiar
              </Button>
            }
            contentSx={{ backgroundColor: theme.palette.background.wallpaper }}
          >
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {camposCliente.map((campo) => (
                <Grid item xs={12} sm={6} lg={4} key={campo.label}>
                  <InfoField
                    label={campo.label}
                    value={campo.value}
                    tooltip={campo.tooltip ?? campo.value}
                    copyValue={campo.copiavel ? campo.value : undefined}
                    setSnackbar={setSnackbar}
                  >
                    {campo.children}
                  </InfoField>
                </Grid>
              ))}
            </Grid>
          </SectionCard>

          <SectionCard
            icon={<ArticleOutlined sx={styles.iconSecao} />}
            title="Dados do atendimento"
            contentSx={{ backgroundColor: theme.palette.background.wallpaper }}
            action={
              <Button
                size="small"
                variant="text"
                startIcon={<ContentCopyOutlined sx={{ fontSize: "16px" }} />}
                onClick={() =>
                  copiarCampos("Dados do atendimento", camposAtendimento)
                }
                sx={styles.botaoCopiarSecao}
              >
                Copiar
              </Button>
            }
          >
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {camposAtendimento.map((campo) => (
                <Grid item xs={12} sm={6} lg={4} key={campo.label}>
                  <InfoField
                    label={campo.label}
                    value={campo.value}
                    tooltip={campo.tooltip ?? campo.value}
                    copyValue={campo.copiavel ? campo.value : undefined}
                    setSnackbar={setSnackbar}
                  >
                    {campo.children}
                  </InfoField>
                </Grid>
              ))}
            </Grid>
          </SectionCard>

          <SectionCard
            icon={<DriveFileRenameOutlineOutlined sx={styles.iconSecao} />}
            title="Descrição de abertura"
            action={
              ticketDetails?.descricao_abertura ? (
                <CopyButton
                  value={ticketDetails?.descricao_abertura}
                  setSnackbar={setSnackbar}
                  title="Copiar descrição de abertura"
                  mensagem="Descrição de abertura copiada!"
                />
              ) : null
            }
            contentSx={{ backgroundColor: theme.palette.background.wallpaper }}
          >
            <Box sx={styles.textoLongo}>
              {ticketDetails?.descricao_abertura ? (
                formatDescricao(ticketDetails?.descricao_abertura)
              ) : (
                <Typography sx={styles.textoVazio}>
                  Nenhuma descrição informada.
                </Typography>
              )}
            </Box>
          </SectionCard>

          <SectionCard
            icon={<CheckCircleOutlineOutlined sx={styles.iconSecao} />}
            title="Encerramento"
            contentSx={{ backgroundColor: theme.palette.background.wallpaper }}
            action={
              ticketDetails?.descricao_fechamento ? (
                <CopyButton
                  value={ticketDetails?.descricao_fechamento}
                  setSnackbar={setSnackbar}
                  title="Copiar descrição de encerramento"
                  mensagem="Descrição de encerramento copiada!"
                />
              ) : null
            }
            sx={{ pb: 3 }}
          >
            <Box sx={styles.textoLongo}>
              {ticketDetails?.descricao_fechamento ? (
                formatDescricao(ticketDetails?.descricao_fechamento)
              ) : (
                <Typography sx={styles.textoVazio}>
                  O atendimento ainda não foi encerrado.
                </Typography>
              )}
            </Box>
          </SectionCard>
        </>
      )}

      {erp === "VOALLE" && (
        <>
          <SectionCard
            icon={<ArticleOutlined sx={styles.iconSecao} />}
            title="Informações gerais"
            action={
              <Button
                size="small"
                variant="text"
                startIcon={<ContentCopyOutlined sx={{ fontSize: "16px" }} />}
                onClick={() => copiarCampos("Informações gerais", camposVoalle)}
                sx={styles.botaoCopiarSecao}
              >
                Copiar
              </Button>
            }
          >
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {camposVoalle.map((campo) => (
                <Grid item xs={12} sm={6} lg={4} key={campo.label}>
                  <InfoField
                    label={campo.label}
                    value={campo.value}
                    tooltip={campo.value}
                    copyValue={campo.copiavel ? campo.value : undefined}
                    setSnackbar={setSnackbar}
                  />
                </Grid>
              ))}
            </Grid>
          </SectionCard>

          <SectionCard
            icon={<DriveFileRenameOutlineOutlined sx={styles.iconSecao} />}
            title="Descrição"
            action={
              ticketDetails?.geral?.description ? (
                <CopyButton
                  value={() => htmlForText(ticketDetails?.geral?.description)}
                  setSnackbar={setSnackbar}
                  title="Copiar descrição"
                  mensagem="Descrição copiada!"
                />
              ) : null
            }
            sx={{ pb: 3 }}
          >
            {ticketDetails?.geral?.description ? (
              <Box
                sx={styles.textoLongo}
                dangerouslySetInnerHTML={{
                  __html: htmlForText(ticketDetails?.geral?.description),
                }}
              />
            ) : (
              <Typography sx={styles.textoVazio}>
                Nenhuma descrição informada.
              </Typography>
            )}
          </SectionCard>
        </>
      )}

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
